export type DriftHandoffSource = 'dashboard' | 'portfolio-analytics' | 'goals';
export type DriftHandoffDestination =
  | 'goals'
  | 'rebalancing-targets'
  | 'rebalancing'
  | 'rebalancing-planner';
export type DriftDominantAction =
  | 'review-drift'
  | 'save-targets'
  | 'review-largest-drifts'
  | 'review-suggested-trades';

export interface DriftHandoffContext {
  source: DriftHandoffSource;
  destination: DriftHandoffDestination;
  proofLabel: string;
  proofOccurredAt?: string | null;
  activeGoalName?: string | null;
  tolerancePct?: number | null;
  driftedAssetCount: number;
  topDriftSymbol?: string | null;
  topDriftPct?: number | null;
  dominantAction: DriftDominantAction;
}

const DRIFT_HANDOFF_SOURCES: DriftHandoffSource[] = ['dashboard', 'portfolio-analytics', 'goals'];
const DRIFT_HANDOFF_DESTINATIONS: DriftHandoffDestination[] = [
  'goals',
  'rebalancing-targets',
  'rebalancing',
  'rebalancing-planner',
];
const DRIFT_DOMINANT_ACTIONS: DriftDominantAction[] = [
  'review-drift',
  'save-targets',
  'review-largest-drifts',
  'review-suggested-trades',
];

function setIfPresent(
  params: URLSearchParams,
  key: string,
  value: string | number | null | undefined,
) {
  if (value === null || value === undefined || value === '') return;
  params.set(key, String(value));
}

export function getDriftHandoffPath(context: DriftHandoffContext): string {
  const params = new URLSearchParams();
  params.set('handoff', 'drift-closure');
  params.set('source', context.source);
  params.set('destination', context.destination);
  params.set('proof', context.proofLabel);
  params.set('drifted', String(context.driftedAssetCount));
  params.set('action', context.dominantAction);
  setIfPresent(params, 'goal', context.activeGoalName);
  setIfPresent(params, 'occurredAt', context.proofOccurredAt);
  setIfPresent(params, 'tolerance', context.tolerancePct);
  setIfPresent(params, 'topSymbol', context.topDriftSymbol);
  setIfPresent(params, 'topDriftPct', context.topDriftPct);

  const destinationPath =
    context.destination === 'goals'
      ? '/goals'
      : context.destination === 'rebalancing'
      ? '/rebalancing'
      : context.destination === 'rebalancing-planner'
        ? '/rebalancing-planner'
        : '/rebalancing-targets';

  return `${destinationPath}?${params.toString()}`;
}

export function readDriftHandoff(searchParams: URLSearchParams): DriftHandoffContext | null {
  if (searchParams.get('handoff') !== 'drift-closure') {
    return null;
  }

  const source = searchParams.get('source');
  const action = searchParams.get('action');
  const proof = searchParams.get('proof');

  if (
    !source ||
    !proof ||
    !action ||
    !DRIFT_HANDOFF_SOURCES.includes(source as DriftHandoffSource) ||
    !DRIFT_DOMINANT_ACTIONS.includes(action as DriftDominantAction)
  ) {
    return null;
  }

  const rawDestination = searchParams.get('destination');
  const destination = DRIFT_HANDOFF_DESTINATIONS.includes(rawDestination as DriftHandoffDestination)
    ? (rawDestination as DriftHandoffDestination)
    : 'rebalancing-targets';
  const driftedAssetCount = Number(searchParams.get('drifted') ?? 0);
  const rawTolerancePct = searchParams.get('tolerance');
  const tolerancePct = rawTolerancePct === null ? null : Number(rawTolerancePct);
  const rawTopDriftPct = searchParams.get('topDriftPct');
  const topDriftPct = rawTopDriftPct === null ? null : Number(rawTopDriftPct);

  return {
    source: source as DriftHandoffSource,
    destination,
    proofLabel: proof,
    proofOccurredAt: searchParams.get('occurredAt'),
    activeGoalName: searchParams.get('goal'),
    tolerancePct: tolerancePct === null || Number.isFinite(tolerancePct) ? tolerancePct : null,
    driftedAssetCount: Number.isFinite(driftedAssetCount) ? driftedAssetCount : 0,
    topDriftSymbol: searchParams.get('topSymbol'),
    topDriftPct: topDriftPct === null || Number.isFinite(topDriftPct) ? topDriftPct : null,
    dominantAction: action as DriftDominantAction,
  };
}

export function hasUsableDriftTargets(targetWeights: Array<number | null | undefined>): boolean {
  return targetWeights.some((targetWeight) => typeof targetWeight === 'number' && targetWeight > 0);
}

export function getDriftDestination(hasUsableTargets: boolean): DriftHandoffDestination {
  return hasUsableTargets ? 'rebalancing' : 'rebalancing-targets';
}

export function getMonitoringDriftDestination({
  activeGoalName,
  hasUsableTargets,
}: {
  activeGoalName?: string | null;
  hasUsableTargets: boolean;
}): DriftHandoffDestination {
  return activeGoalName ? 'goals' : getDriftDestination(hasUsableTargets);
}

export function getDriftDominantAction({
  hasUsableTargets,
  hasSuggestedTrades,
}: {
  hasUsableTargets: boolean;
  hasSuggestedTrades: boolean;
}): DriftDominantAction {
  if (!hasUsableTargets) {
    return 'save-targets';
  }

  return hasSuggestedTrades ? 'review-suggested-trades' : 'review-largest-drifts';
}

export function getMonitoringDriftDominantAction({
  destination,
  hasUsableTargets,
  hasSuggestedTrades,
}: {
  destination: DriftHandoffDestination;
  hasUsableTargets: boolean;
  hasSuggestedTrades: boolean;
}): DriftDominantAction {
  if (destination === 'goals') {
    return 'review-drift';
  }

  return getDriftDominantAction({
    hasUsableTargets,
    hasSuggestedTrades,
  });
}

export function buildDriftProofLabel({
  activeGoalName,
  driftedAssetCount,
  usableTargetCount,
}: {
  activeGoalName?: string | null;
  driftedAssetCount: number;
  usableTargetCount: number;
}): string {
  if (activeGoalName) {
    return `${activeGoalName} drift proof remains active`;
  }

  if (driftedAssetCount > 0) {
    return `${driftedAssetCount} asset${driftedAssetCount === 1 ? '' : 's'} outside tolerance`;
  }

  return `Saved targets active across ${usableTargetCount} allocation${usableTargetCount === 1 ? '' : 's'}`;
}

export function getLargestDriftInsight<T>(
  rows: T[],
  getSymbol: (row: T) => string,
  getDriftPct: (row: T) => number,
): { symbol: string | null; driftPct: number | null } {
  if (rows.length === 0) {
    return { symbol: null, driftPct: null };
  }

  const [topRow] = [...rows].sort((left, right) => Math.abs(getDriftPct(right)) - Math.abs(getDriftPct(left)));
  return {
    symbol: getSymbol(topRow) ?? null,
    driftPct: getDriftPct(topRow),
  };
}

export function getDriftSourceLabel(source: DriftHandoffSource): string {
  if (source === 'portfolio-analytics') return 'Portfolio Analytics';
  if (source === 'goals') return 'Goals';
  return 'Dashboard';
}

export function getDriftDominantActionLabel(action: DriftDominantAction): string {
  if (action === 'review-drift') return 'Review Drift';
  if (action === 'save-targets') return 'Save targets';
  if (action === 'review-largest-drifts') return 'Review largest drifts';
  return 'Review suggested trades';
}

export function getDriftDominantActionDescription(action: DriftDominantAction): string {
  if (action === 'review-drift') {
    return 'Review the mounted goal drift first so the saved goal remains the authoritative owner before advisory follow-up.';
  }

  if (action === 'save-targets') {
    return 'Start by confirming target intent here before treating any trade output as ready.';
  }

  if (action === 'review-largest-drifts') {
    return 'Start with the biggest out-of-tolerance assets first so the highest urgency is visible immediately.';
  }

  return 'Start with the advisory trade stack only after saved target intent is already trustworthy.';
}

export function formatDriftProofOccurredAt(value?: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface DriftProofComparison {
  proofLabel: string;
  proofOccurredAt?: string | null;
  activeGoalName?: string | null;
  tolerancePct?: number | null;
  driftedAssetCount: number;
  topDriftSymbol?: string | null;
  topDriftPct?: number | null;
}

function normalizeProofValue(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeProofPct(value?: number | null): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(4) : null;
}

export function driftProofsMatch(
  left?: DriftProofComparison | null,
  right?: DriftProofComparison | null,
): boolean {
  if (!left || !right) {
    return false;
  }

  return (
    normalizeProofValue(left.activeGoalName) === normalizeProofValue(right.activeGoalName)
    && normalizeProofValue(left.proofLabel) === normalizeProofValue(right.proofLabel)
    && normalizeProofValue(left.proofOccurredAt) === normalizeProofValue(right.proofOccurredAt)
    && normalizeProofPct(left.tolerancePct) === normalizeProofPct(right.tolerancePct)
    && left.driftedAssetCount === right.driftedAssetCount
    && normalizeProofValue(left.topDriftSymbol) === normalizeProofValue(right.topDriftSymbol)
    && normalizeProofPct(left.topDriftPct) === normalizeProofPct(right.topDriftPct)
  );
}
